import { tr, useT } from '@/i18n';
import shared from '@/pages/pages.module.css';

import chat from './chat.module.css';

import { AppCard, FlowGuide } from './AppCard';
import { BackButton } from './BackButton';
import { Button } from './Button';
import { GUEST_TOPIC } from './ChatView';
import { ArrowUpIcon, EyeOffIcon, LockIcon, PaperclipIcon } from './Icons';
import styles from './OpenGate.module.css';
import type { OpenGateProps } from './OpenGate.types';

/** Two message rows drawn as outlines: the shape of a conversation, with nothing of one in it. */
const ROWS = [
  { name: 56, lines: [88, 46] },
  { name: 40, lines: [72] },
] as const;

/**
 * The screen a chat link opens on: a view is spent and the chat shown only after this button. A link scanner or
 * preview bot that runs the page's script (and sees the key in the fragment) then finds neither a spent view nor the
 * conversation. It is the opened chat's own window (the same head, list and composer, drawn locked and empty, down to
 * the height of each part), so only the content changes when the chat opens and nothing moves.
 */
export function OpenGate({ onOpen }: OpenGateProps): React.JSX.Element {
  const t = useT();
  return (
    <AppCard guide={<FlowGuide flow="guestGate" progress={0} />} testId="open-gate" stepKey="open-gate" direction="back">
      <div className={chat.chat}>
        <header className={chat.head}>
          <span className={chat.headBack}>
            <BackButton />
          </span>
          <div className={chat.headText}>
            <h2 className={chat.title}>
              {tr('openGate.title', { break: () => <br className={shared.phoneBreak} /> })}
            </h2>
            <p className={chat.topic}>{t(GUEST_TOPIC)}</p>
          </div>
          <div className={chat.headActions}>
            <button type="button" className={[chat.headButton, styles.dim].join(' ')} disabled aria-label={t('openGate.hideLocked')}>
              <EyeOffIcon size={16} />
            </button>
          </div>
        </header>

        <div className={chat.list} data-testid="locked-chat">
          <div className={styles.rows} aria-hidden="true">
            {ROWS.map((row, index) => (
              <div key={index} className={styles.row}>
                <span className={styles.face} />
                <div className={styles.lines}>
                  <span className={styles.bar} style={{ width: `${String(row.name)}px` }} />
                  {row.lines.map((width, line) => (
                    <span key={line} className={styles.bar} style={{ width: `${String(width)}%` }} />
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className={styles.open}>
            <Button onClick={onOpen} data-testid="open-bridge">
              {t('openGate.open')}
            </Button>
            <span className={styles.why}>
              <LockIcon size={14} />
              {t('openGate.locked')}
            </span>
          </div>
        </div>

        <div className={[chat.composer, styles.faded].join(' ')} aria-disabled="true">
          <textarea className={chat.field} rows={1} disabled placeholder={t('openGate.placeholder')} aria-label={t('openGate.messageLocked')} />
          <div className={chat.bar}>
            <button type="button" className={chat.tool} disabled aria-label={t('openGate.attachLocked')}>
              <PaperclipIcon size={18} />
            </button>
            <button type="button" className={chat.send} disabled aria-label={t('openGate.sendLocked')}>
              <ArrowUpIcon size={18} strokeWidth={2.2} />
            </button>
          </div>
        </div>
      </div>
    </AppCard>
  );
}
